import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import QuickActionButton from '../../components/ui/QuickActionButton';
import WorkoutCard from './components/WorkoutCard';
import FilterPanel from './components/FilterPanel';
import FeaturedSection from './components/FeaturedSection';
import WorkoutPreviewModal from './components/WorkoutPreviewModal';
import QuickFilters from './components/QuickFilters';
import Icon from '../../components/AppIcon';

const WorkoutLibrary = () => {
  const navigate = useNavigate();
  const [selectedWorkout, setSelectedWorkout] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [quickFilter, setQuickFilter] = useState('all');
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    difficulty: 'all',
    duration: 'all',
    equipment: 'all'
  });

  const workouts = [
  {
    id: 1,
    title: "Full Body Strength Builder",
    description: "A comprehensive strength training program targeting all major muscle groups with compound movements for maximum efficiency and muscle development.",
    image: "https://images.unsplash.com/photo-1674748596342-8fd299450a71",
    imageAlt: "Athletic man performing barbell deadlift exercise in modern gym with natural lighting and equipment in background",
    category: "strength",
    difficulty: "Intermediate",
    duration: "45 min",
    exercises: 8,
    calories: 420,
    rating: 4.8,
    reviews: 234,
    completions: "2.3k",
    equipment: ["Barbell", "Dumbbells", "Bench"],
    isFavorite: true,
    isNew: false,
    exerciseList: [
    { name: "Barbell Squats", sets: "4 sets × 8-10 reps" },
    { name: "Bench Press", sets: "4 sets × 8-10 reps" },
    { name: "Deadlifts", sets: "3 sets × 6-8 reps" },
    { name: "Overhead Press", sets: "3 sets × 8-10 reps" },
    { name: "Bent-Over Rows", sets: "4 sets × 8-10 reps" },
    { name: "Pull-ups", sets: "3 sets × max reps" },
    { name: "Dumbbell Lunges", sets: "3 sets × 10 reps each leg" },
    { name: "Plank Hold", sets: "3 sets × 60 seconds" }]

  },
  {
    id: 2,
    title: "HIIT Cardio Blast",
    description: "High-intensity interval training designed to maximize calorie burn and improve cardiovascular endurance in minimal time with explosive movements.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1053e57c3-1767786759759.png",
    imageAlt: "Energetic woman performing jumping exercise during high-intensity interval training in bright fitness studio with wooden floors",
    category: "hiit",
    difficulty: "Advanced",
    duration: "30 min",
    exercises: 10,
    calories: 380,
    rating: 4.9,
    reviews: 456,
    completions: "4.1k",
    equipment: ["None"],
    isFavorite: false,
    isNew: true,
    exerciseList: [
    { name: "Burpees", sets: "4 sets × 45 seconds" },
    { name: "Mountain Climbers", sets: "4 sets × 45 seconds" },
    { name: "Jump Squats", sets: "4 sets × 45 seconds" },
    { name: "High Knees", sets: "4 sets × 45 seconds" },
    { name: "Plank Jacks", sets: "4 sets × 45 seconds" },
    { name: "Box Jumps", sets: "4 sets × 45 seconds" },
    { name: "Russian Twists", sets: "4 sets × 45 seconds" },
    { name: "Jumping Lunges", sets: "4 sets × 45 seconds" },
    { name: "Push-up to T", sets: "4 sets × 45 seconds" },
    { name: "Sprint in Place", sets: "4 sets × 45 seconds" }]

  },
  {
    id: 3,
    title: "Yoga Flow for Flexibility",
    description: "Gentle yet effective yoga sequence focusing on improving flexibility, balance, and mindfulness through traditional poses and breathing techniques.",
    image: "https://images.unsplash.com/photo-1561577631-0dc82f681d60",
    imageAlt: "Peaceful woman in warrior pose during yoga practice on mat in serene studio with natural light streaming through windows",
    category: "yoga",
    difficulty: "Beginner",
    duration: "40 min",
    exercises: 12,
    calories: 180,
    rating: 4.7,
    reviews: 189,
    completions: "1.8k",
    equipment: ["Yoga Mat"],
    isFavorite: true,
    isNew: false,
    exerciseList: [
    { name: "Sun Salutation A", sets: "3 rounds" },
    { name: "Warrior I", sets: "Hold 5 breaths each side" },
    { name: "Warrior II", sets: "Hold 5 breaths each side" },
    { name: "Triangle Pose", sets: "Hold 5 breaths each side" },
    { name: "Tree Pose", sets: "Hold 5 breaths each side" },
    { name: "Downward Dog", sets: "Hold 10 breaths" },
    { name: "Child\'s Pose", sets: "Hold 10 breaths" },
    { name: "Cobra Pose", sets: "Hold 5 breaths" },
    { name: "Pigeon Pose", sets: "Hold 10 breaths each side" },
    { name: "Seated Forward Fold", sets: "Hold 10 breaths" },
    { name: "Spinal Twist", sets: "Hold 5 breaths each side" },
    { name: "Savasana", sets: "5 minutes" }]

  },
  {
    id: 4,
    title: "Upper Body Power",
    description: "Intensive upper body workout focusing on chest, back, shoulders, and arms with progressive overload principles for strength gains.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d9390292-1764649132607.png",
    imageAlt: "Muscular man performing dumbbell shoulder press exercise in professional gym with focused expression and proper form",
    category: "strength",
    difficulty: "Advanced",
    duration: "50 min",
    exercises: 9,
    calories: 450,
    rating: 4.9,
    reviews: 312,
    completions: "2.7k",
    equipment: ["Dumbbells", "Pull-up Bar", "Cable Machine"],
    isFavorite: false,
    isNew: false,
    exerciseList: [
    { name: "Incline Dumbbell Press", sets: "4 sets × 8-10 reps" },
    { name: "Weighted Pull-ups", sets: "4 sets × 6-8 reps" },
    { name: "Military Press", sets: "4 sets × 8-10 reps" },
    { name: "Cable Rows", sets: "4 sets × 10-12 reps" },
    { name: "Dumbbell Flyes", sets: "3 sets × 12 reps" },
    { name: "Face Pulls", sets: "3 sets × 15 reps" },
    { name: "Barbell Curls", sets: "3 sets × 10 reps" },
    { name: "Tricep Dips", sets: "3 sets × 12 reps" },
    { name: "Lateral Raises", sets: "3 sets × 15 reps" }]

  },
  {
    id: 5,
    title: "Core Stability Challenge",
    description: "Targeted core workout combining static holds and dynamic movements to build a strong, stable midsection and improve overall functional fitness.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f0985ea-1768653516252.png",
    imageAlt: "Fit woman performing plank exercise on yoga mat in bright gym showing perfect form and core engagement",
    category: "strength",
    difficulty: "Intermediate",
    duration: "25 min",
    exercises: 8,
    calories: 220,
    rating: 4.6,
    reviews: 167,
    completions: "1.5k",
    equipment: ["Exercise Mat", "Medicine Ball"],
    isFavorite: false,
    isNew: true,
    exerciseList: [
    { name: "Plank Hold", sets: "4 sets × 60 seconds" },
    { name: "Russian Twists", sets: "4 sets × 20 reps" },
    { name: "Bicycle Crunches", sets: "4 sets × 30 reps" },
    { name: "Dead Bug", sets: "3 sets × 15 reps each side" },
    { name: "Mountain Climbers", sets: "4 sets × 30 seconds" },
    { name: "Side Plank", sets: "3 sets × 45 seconds each side" },
    { name: "Medicine Ball Slams", sets: "4 sets × 15 reps" },
    { name: "Hollow Body Hold", sets: "3 sets × 30 seconds" }]

  },
  {
    id: 6,
    title: "Beginner\'s Total Body",
    description: "Perfect introduction to strength training with fundamental exercises and proper form guidance for building a solid fitness foundation.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_10431de97-1766586472264.png",
    imageAlt: "Smiling beginner performing bodyweight squat exercise with trainer guidance in welcoming gym environment",
    category: "strength",
    difficulty: "Beginner",
    duration: "35 min",
    exercises: 7,
    calories: 280,
    rating: 4.8,
    reviews: 423,
    completions: "5.2k",
    equipment: ["Dumbbells", "Exercise Mat"],
    isFavorite: true,
    isNew: false,
    exerciseList: [
    { name: "Bodyweight Squats", sets: "3 sets × 12 reps" },
    { name: "Push-ups", sets: "3 sets × 10 reps" },
    { name: "Dumbbell Rows", sets: "3 sets × 12 reps each arm" },
    { name: "Lunges", sets: "3 sets × 10 reps each leg" },
    { name: "Shoulder Press", sets: "3 sets × 10 reps" },
    { name: "Plank", sets: "3 sets × 30 seconds" },
    { name: "Glute Bridges", sets: "3 sets × 15 reps" }]

  },
  {
    id: 7,
    title: "Cardio Endurance Run",
    description: "Progressive running program designed to build cardiovascular endurance and stamina through interval training and steady-state cardio.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_118d6e19f-1769169302705.png",
    imageAlt: "Athletic runner in motion on outdoor track during sunrise with determined expression and proper running form",
    category: "cardio",
    difficulty: "Intermediate",
    duration: "40 min",
    exercises: 5,
    calories: 400,
    rating: 4.7,
    reviews: 278,
    completions: "3.4k",
    equipment: ["None"],
    isFavorite: false,
    isNew: false,
    exerciseList: [
    { name: "Warm-up Jog", sets: "5 minutes" },
    { name: "Interval Sprints", sets: "8 × 1 minute fast, 2 minutes recovery" },
    { name: "Steady-State Run", sets: "15 minutes at moderate pace" },
    { name: "Hill Repeats", sets: "5 × 2 minutes uphill" },
    { name: "Cool-down Walk", sets: "5 minutes" }]

  },
  {
    id: 8,
    title: "Pilates Core Fusion",
    description: "Mat-based Pilates workout emphasizing controlled movements, breath work, and core engagement for improved posture and body awareness.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_149ef5ba4-1767725368107.png",
    imageAlt: "Woman performing pilates hundred exercise on mat with focused concentration in peaceful studio setting",
    category: "pilates",
    difficulty: "Beginner",
    duration: "35 min",
    exercises: 10,
    calories: 200,
    rating: 4.8,
    reviews: 201,
    completions: "2.1k",
    equipment: ["Pilates Mat", "Resistance Band"],
    isFavorite: true,
    isNew: true,
    exerciseList: [
    { name: "The Hundred", sets: "3 sets" },
    { name: "Roll Up", sets: "10 reps" },
    { name: "Single Leg Circles", sets: "10 reps each leg" },
    { name: "Rolling Like a Ball", sets: "10 reps" },
    { name: "Single Leg Stretch", sets: "10 reps each leg" },
    { name: "Double Leg Stretch", sets: "10 reps" },
    { name: "Spine Stretch Forward", sets: "8 reps" },
    { name: "Swan Dive", sets: "8 reps" },
    { name: "Side Kick Series", sets: "10 reps each side" },
    { name: "Seal", sets: "10 reps" }]

  }];


  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      category: 'all',
      difficulty: 'all',
      duration: 'all',
      equipment: 'all'
    });
    setQuickFilter('all');
  };

  const handleFavoriteToggle = (workoutId) => {
    const workout = workouts?.find((w) => w?.id === workoutId);
    if (workout) {
      workout.isFavorite = !workout?.isFavorite;
    }
  };

  const handlePreview = (workout) => {
    setSelectedWorkout(workout);
  };

  const handleStartWorkout = (workout) => {
    navigate('/active-workout', { state: { workout } });
  };

  const workoutsWithHandlers = workouts?.map((workout) => ({
    ...workout,
    onPreview: handlePreview
  }));

  const filteredWorkouts = useMemo(() => {
    let filtered = [...workoutsWithHandlers];

    if (quickFilter === 'favorites') {
      filtered = filtered?.filter((w) => w?.isFavorite);
    } else if (quickFilter === 'recent') {
      filtered = filtered?.slice(0, 4);
    } else if (quickFilter === 'popular') {
      filtered = filtered?.sort((a, b) => b?.rating - a?.rating)?.slice(0, 6);
    }

    if (filters?.search) {
      const searchLower = filters?.search?.toLowerCase();
      filtered = filtered?.filter((w) =>
      w?.title?.toLowerCase()?.includes(searchLower) ||
      w?.description?.toLowerCase()?.includes(searchLower)
      );
    }

    if (filters?.category !== 'all') {
      filtered = filtered?.filter((w) => w?.category === filters?.category);
    }

    if (filters?.difficulty !== 'all') {
      filtered = filtered?.filter((w) => w?.difficulty?.toLowerCase() === filters?.difficulty);
    }

    if (filters?.duration !== 'all') {
      const [min, max] = filters?.duration?.split('-')?.map((v) => parseInt(v) || Infinity);
      filtered = filtered?.filter((w) => {
        const duration = parseInt(w?.duration);
        return duration >= min && (max === Infinity || duration <= max);
      });
    }

    if (filters?.equipment !== 'all') {
      if (filters?.equipment === 'none') {
        filtered = filtered?.filter((w) => w?.equipment?.length === 1 && w?.equipment?.[0] === 'None');
      } else {
        filtered = filtered?.filter((w) =>
        w?.equipment?.some((e) => e?.toLowerCase()?.includes(filters?.equipment))
        );
      }
    }

    return filtered;
  }, [workoutsWithHandlers, filters, quickFilter]);

  const featuredWorkouts = workoutsWithHandlers?.filter((w) => w?.isNew || w?.rating >= 4.8)?.slice(0, 1);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-[1440px] px-4 md:px-6 lg:px-8 py-6 lg:py-8 pb-20 lg:pb-8">
        <div className="mb-6 lg:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-2">
            Workout Library
          </h1>
          <p className="text-base md:text-lg text-muted-foreground">
            Discover and start your perfect workout from our comprehensive collection
          </p>
        </div>

        <FeaturedSection
          workouts={featuredWorkouts}
          onStartWorkout={handleStartWorkout} />


        <QuickFilters
          activeFilter={quickFilter}
          onFilterChange={setQuickFilter} />


        <div className="grid lg:grid-cols-[320px_1fr] gap-6 lg:gap-8">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <FilterPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onClearFilters={handleClearFilters}
              resultCount={filteredWorkouts?.length}
              isOpen={isFilterOpen}
              onToggle={() => setIsFilterOpen(!isFilterOpen)} />

          </aside>

          <div>
            {filteredWorkouts?.length === 0 ?
            <div className="flex flex-col items-center justify-center py-16 lg:py-24">
                <div className="flex items-center justify-center w-20 h-20 bg-muted rounded-full mb-4">
                  <Icon name="Search" size={40} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
                  No workouts found
                </h3>
                <p className="text-muted-foreground text-center mb-6 max-w-md">
                  Try adjusting your filters or search terms to find the perfect workout
                </p>
                <button
                onClick={handleClearFilters}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-smooth press-scale">

                  Clear All Filters
                </button>
              </div> :

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5 lg:gap-6">
                {filteredWorkouts?.map((workout) =>
              <WorkoutCard
                key={workout?.id}
                workout={workout}
                onFavoriteToggle={handleFavoriteToggle} />

              )}
              </div>
            }
          </div>
        </div>
      </main>
      {selectedWorkout &&
      <WorkoutPreviewModal
        workout={selectedWorkout}
        onClose={() => setSelectedWorkout(null)} />

      }
      <MobileBottomNav />
      <QuickActionButton />
    </div>);

};

export default WorkoutLibrary;